import { createApi } from '@reduxjs/toolkit/query/react';

import {
  getBaseQuery,
  getListProvidesTags,
  TransformedErrorResponseWithNotification,
  TransformedResponseWithNotification,
} from '../generic';
import * as endpoints from '../endpoints';
import { REDUX_CACHE, TRANSLATIONS, HTTP_METHOD } from '../constants';
import { ITranslation } from '../../../types/interfaces/translation';
import { ILanguage } from '../../../types/interfaces/language';

export const translationsApi = createApi({
  reducerPath: 'translationsApi',
  tagTypes: [
    TRANSLATIONS.TRANSLATIONS,
    TRANSLATIONS.LANGUAGES,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery(),
  // global configuration for the api
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getTranslationsApi: builder.query<
      ITranslation[],
      { withLanguage?: boolean } | void
    >({
      keepUnusedDataFor: 3600,
      query(params) {
        const withLanguage = !!params?.['withLanguage'];

        return {
          url: `${endpoints.Translation.ROOT}${withLanguage ? '?' + 'withLanguage=true' : ''}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, TRANSLATIONS.TRANSLATIONS),
      ...TransformedErrorResponseWithNotification(),
    }),
    getLanguagesApi: builder.query<ILanguage[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.Language.ROOT,
          method: HTTP_METHOD.GET,
          headers: {},
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, TRANSLATIONS.LANGUAGES),
      ...TransformedErrorResponseWithNotification(),
    }),
    postPutTranslation: builder.mutation<
      void,
      { id: string; body: ITranslation }
    >({
      query({ id, body }) {
        return {
          url: id
            ? `${endpoints.Translation.ROOT}/${id}`
            : endpoints.Translation.ROOT,
          method: !id ? HTTP_METHOD.POST : HTTP_METHOD.PUT,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRANSLATIONS.TRANSLATIONS,
              id: REDUX_CACHE.LIST,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Translation'),
    }),
    deleteTranslation: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: `${endpoints.Translation.ROOT}/${id}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: TRANSLATIONS.TRANSLATIONS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedResponseWithNotification('Translation'),
    }),
  }),
});

export const {
  useGetTranslationsApiQuery: useGetTranslations,
  usePostPutTranslationMutation: usePostPutTranslation,
  useDeleteTranslationMutation: useDeleteTranslation,
  useGetLanguagesApiQuery: useGetLanguages,
  useLazyGetLanguagesApiQuery: useLazyGetLanguages,
} = translationsApi;
